import { useEffect, useState, useMemo, type JSX } from "react";
import { loadClientEnv } from "../../config/env";
import {
  parseInlineMarkup,
  stripInlineMarkup,
  type CharacterEntry,
} from "./parseInlineMarkup";

export type ChoiceVisualCue = {
  characterId: string;
  actingVariantId: string;
  previewLayer?: "standing" | "cutin" | "none" | undefined;
  confirmLayer?: "standing" | "cutin" | "none" | undefined;
};

export type ChoiceListChoice = {
  id: string;
  text: string;
  emotion?: string | undefined;
  requiresMemory?: string | string[] | undefined;
  visualCue?: ChoiceVisualCue | undefined;
};

export type ChoicePreview = {
  id: string;
  text: string;
  emotion?: string | undefined;
  visualCue?: ChoiceVisualCue | undefined;
};

export type ChoiceListProps = {
  choices: ChoiceListChoice[];
  isPending: boolean;
  onSelect: (choiceId: string) => void;
  // backscroll-history: 過去シーンで既に選ばれた choice を視覚的に強調 + 非該当をグレーアウト。
  // 指定時（backscroll 経路）は 2段階タップを無効化し 1 タップで即時 onSelect する。
  // 注: reader-ux 改修（番号バッジ追加）以降、未指定でも旧 DOM との
  // bit-by-bit 互換は保っていない。
  chosenChoiceId?: string | undefined;
  // keyword-highlight: キャラ色解決用 Map。
  charactersMap?: Map<string, CharacterEntry> | undefined;
  /** visual_novel の固定テキスト領域では compact、ステージ横パネルでは side にして選択肢を収める。 */
  variant?: "default" | "compact" | "side";
  /** visual_novel side variant で選択肢の直前に出す短い内心 prompt。 */
  choicePrompt?: string | undefined;
  /** 1タップ目の preview を親レイヤーへ通知する。visual_novel のステージ overlay で使う。 */
  onPreviewChoiceChange?: ((preview: ChoicePreview | null) => void) | undefined;
};

const EMPTY_CHARACTERS_MAP: Map<string, CharacterEntry> = new Map();
type ChoiceListVariant = NonNullable<ChoiceListProps["variant"]>;
type ChoiceChosenAttr = "true" | "false" | undefined;

type ChoiceRenderState = {
  isChosen: boolean;
  isNonChosen: boolean;
  isPreviewed: boolean;
  isDimmed: boolean;
  chosenAttr: ChoiceChosenAttr;
};

type ChoiceOptionProps = {
  choice: ChoiceListChoice;
  index: number;
  variant: ChoiceListVariant;
  state: ChoiceRenderState;
  isPending: boolean;
  hasChosenContext: boolean;
  showEmotion: boolean;
  charactersMap: Map<string, CharacterEntry>;
  onTap: (choiceId: string) => void;
};

function isCompactVariant(variant: ChoiceListVariant): boolean {
  return variant === "compact";
}

function isStageSideVariant(variant: ChoiceListVariant): boolean {
  return variant === "side";
}

function shouldNotifyPreview(variant: ChoiceListVariant): boolean {
  return isCompactVariant(variant) || isStageSideVariant(variant);
}

function getChoiceListOptionsClassName(variant: ChoiceListVariant): string {
  if (isCompactVariant(variant)) {
    return "grid grid-cols-2 gap-2";
  }
  if (isStageSideVariant(variant)) {
    return "flex flex-col gap-2 px-3 py-1.5";
  }
  return "flex flex-col gap-2";
}

function getChoiceButtonBaseClassName(variant: ChoiceListVariant): string {
  if (isCompactVariant(variant)) {
    return "relative h-12 min-h-12 w-full overflow-hidden text-left rounded-md border flex items-center gap-2 px-2.5 py-2 ";
  }
  if (isStageSideVariant(variant)) {
    return "relative min-h-12 w-full overflow-hidden text-left rounded-md border flex items-center gap-2.5 px-3 py-2.5 md:min-h-[3.35rem] md:gap-3 md:px-3.5 md:py-3 ";
  }
  return "choice-enter w-full text-left rounded-lg border flex items-start gap-3 px-4 py-3.5 ";
}

function getBadgeBaseClassName(variant: ChoiceListVariant): string {
  if (isCompactVariant(variant)) {
    return "shrink-0 size-5 rounded-full border text-[11px] font-bold flex items-center justify-center transition-colors duration-200 ";
  }
  if (isStageSideVariant(variant)) {
    return "shrink-0 size-5 rounded-full border bg-slate-950/22 text-[11px] font-bold flex items-center justify-center transition-colors duration-200 md:size-6 md:text-xs ";
  }
  return "shrink-0 w-6 h-6 rounded-full border text-xs font-bold flex items-center justify-center mt-0.5 transition-colors duration-200 ";
}

function getTextStackClassName(variant: ChoiceListVariant): string {
  return isCompactVariant(variant) || isStageSideVariant(variant)
    ? "flex-1 flex min-w-0 flex-col"
    : "flex-1 flex flex-col gap-1";
}

function getChoiceTextClassName(variant: ChoiceListVariant): string {
  if (isCompactVariant(variant)) {
    return "truncate whitespace-nowrap text-xs font-medium text-reader-primary leading-tight md:text-sm";
  }
  if (isStageSideVariant(variant)) {
    return "text-sm font-medium leading-snug text-current drop-shadow-[0_1px_3px_rgba(0,0,0,0.92)] md:text-[0.95rem]";
  }
  return "font-medium text-reader-primary leading-snug";
}

function getEmotionClassName(variant: ChoiceListVariant): string {
  return isCompactVariant(variant)
    ? "line-clamp-1 text-xs text-reader-muted leading-snug transition-opacity duration-200"
    : "text-sm text-reader-muted leading-relaxed transition-opacity duration-200";
}

function getPreviewHintClassName(variant: ChoiceListVariant): string {
  return isCompactVariant(variant)
    ? "text-[11px] font-medium leading-tight text-reader-muted transition-opacity duration-200"
    : "text-sm text-reader-focus-ring font-medium transition-opacity duration-200";
}

function getCompactPreviewLineClassName(): string {
  return "min-h-5 truncate whitespace-nowrap px-1 text-sm font-medium leading-snug text-slate-100/95";
}

function getChoicePromptClassName(): string {
  return "relative mb-0.5 w-fit max-w-full rounded-full bg-slate-950/72 px-3 py-1 text-sm font-semibold leading-snug text-slate-50 shadow-[0_10px_28px_rgba(2,6,23,0.52),0_0_20px_rgba(15,23,42,0.58)] ring-1 ring-white/12 backdrop-blur-sm [text-shadow:0_2px_8px_rgba(0,0,0,0.95)] md:text-[0.95rem]";
}

function getArrowClassName(variant: ChoiceListVariant, isPreviewed: boolean): string {
  const variantClassName = isCompactVariant(variant) || isStageSideVariant(variant)
    ? "shrink-0 text-base leading-none transition-all duration-200 "
    : "shrink-0 text-lg leading-none mt-0.5 transition-all duration-200 ";

  if (isStageSideVariant(variant)) {
    return variantClassName + (isPreviewed ? "text-slate-50 font-bold" : "text-slate-300/70");
  }
  return variantClassName + (isPreviewed ? "text-reader-focus-ring font-bold" : "text-reader-muted");
}

function shouldRenderInlineEmotion(variant: ChoiceListVariant): boolean {
  return variant === "default";
}

function shouldRenderInlinePreviewHint(variant: ChoiceListVariant): boolean {
  return variant === "default";
}

function shouldRenderLocalPreviewEmotion(variant: ChoiceListVariant): boolean {
  return isCompactVariant(variant);
}

function getChoiceButtonClassName(
  variant: ChoiceListVariant,
  state: ChoiceRenderState,
): string {
  return (
    getChoiceButtonBaseClassName(variant) +
    "transition-all duration-200 " +
    (isStageSideVariant(variant)
      ? "disabled:cursor-not-allowed "
      : "disabled:opacity-50 disabled:cursor-not-allowed ") +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-reader-focus-ring focus-visible:ring-offset-1 focus-visible:ring-offset-reader-page" +
    getChoiceStateClassName(variant, state)
  );
}

function getBadgeClassName(variant: ChoiceListVariant, isPreviewed: boolean): string {
  if (isStageSideVariant(variant)) {
    return (
      getBadgeBaseClassName(variant) +
      (isPreviewed
        ? "border-slate-50/80 bg-slate-50/88 text-slate-950 shadow-[0_0_0_1px_rgba(15,23,42,0.18)]"
        : "border-white/28 text-slate-200")
    );
  }
  return (
    getBadgeBaseClassName(variant) +
    (isPreviewed
      ? "border-reader-focus-ring text-reader-focus-ring"
      : "border-reader-border text-reader-muted")
  );
}

function getChoiceOptionStyle(
  variant: ChoiceListVariant,
  index: number,
): { animationDelay: string } | undefined {
  return isCompactVariant(variant) || isStageSideVariant(variant)
    ? undefined
    : { animationDelay: `${index * 0.15}s` };
}

function shouldUseCompactPreviewEmotion(
  variant: ChoiceListVariant,
  previewedChoice: ChoiceListChoice | undefined,
  choiceEmotionEnabled: boolean,
): boolean {
  return (
    shouldRenderLocalPreviewEmotion(variant) &&
    previewedChoice !== undefined &&
    shouldShowEmotion(previewedChoice, choiceEmotionEnabled)
  );
}

function getPreviewNotification(
  previewedChoice: ChoiceListChoice,
  choiceEmotionEnabled: boolean,
): ChoicePreview {
  const previewEmotion = shouldShowEmotion(previewedChoice, choiceEmotionEnabled)
    ? stripInlineMarkup(previewedChoice.emotion ?? "")
    : undefined;
  return {
    id: previewedChoice.id,
    text: stripInlineMarkup(previewedChoice.text),
    ...(previewEmotion !== undefined && previewEmotion.length > 0
      ? { emotion: previewEmotion }
      : {}),
    ...(previewedChoice.visualCue !== undefined
      ? { visualCue: previewedChoice.visualCue }
      : {}),
  };
}

function getChoiceStateClassName(
  variant: ChoiceListVariant,
  state: ChoiceRenderState,
): string {
  if (isStageSideVariant(variant)) {
    if (state.isChosen) {
      return " relative z-10 scale-[1.02] border-slate-100/62 bg-slate-950/88 text-slate-50 ring-1 ring-inset ring-slate-50/45 shadow-[inset_3px_0_0_rgba(226,232,240,0.78),0_14px_34px_rgba(2,6,23,0.58)] backdrop-blur-md";
    }
    if (state.isNonChosen) {
      return " border-white/16 bg-slate-950/68 text-slate-300/85";
    }
    if (state.isPreviewed) {
      return " relative z-10 scale-[1.035] border-slate-50/82 bg-slate-950/88 text-slate-50 ring-1 ring-inset ring-slate-50/68 shadow-[inset_4px_0_0_rgba(248,250,252,0.92),0_18px_44px_rgba(2,6,23,0.62)] backdrop-blur-md";
    }
    if (state.isDimmed) {
      return " scale-[0.99] border-white/16 bg-slate-950/68 text-slate-300/90";
    }
    return " border-white/32 bg-slate-950/76 text-slate-50 shadow-[inset_0_1px_0_rgba(255,255,255,0.1),0_12px_30px_rgba(2,6,23,0.46)] backdrop-blur-md hover:border-slate-100/54 hover:bg-slate-950/88";
  }
  if (state.isChosen) {
    return " border-reader-warning-border bg-reader-surface ring-1 ring-reader-warning-border/30";
  }
  if (state.isNonChosen) {
    return " opacity-50 border-reader-border bg-reader-surface";
  }
  if (state.isPreviewed) {
    return " border-reader-focus-ring bg-reader-surface scale-[1.02] -translate-y-0.5 shadow-lg";
  }
  if (state.isDimmed) {
    return " opacity-50 scale-[0.97] border-reader-border bg-reader-surface";
  }
  return " border-reader-border bg-reader-surface hover:bg-reader-border/60 hover:border-reader-focus-ring/50";
}

function getChoiceRenderState(
  choiceId: string,
  chosenChoiceId: string | undefined,
  previewedId: string | null,
  hasChosenContext: boolean,
): ChoiceRenderState {
  const isChosen = hasChosenContext && chosenChoiceId === choiceId;
  const isNonChosen = hasChosenContext && chosenChoiceId !== choiceId;
  const isPreviewed = !hasChosenContext && previewedId === choiceId;
  const isDimmed = !hasChosenContext && previewedId !== null && !isPreviewed;
  const chosenAttr: ChoiceChosenAttr = isChosen ? "true" : isNonChosen ? "false" : undefined;

  return { isChosen, isNonChosen, isPreviewed, isDimmed, chosenAttr };
}

function shouldShowEmotion(choice: ChoiceListChoice, choiceEmotionEnabled: boolean): boolean {
  return (
    choiceEmotionEnabled &&
    typeof choice.emotion === "string" &&
    choice.emotion.length > 0
  );
}

export function resolveChoiceThinkingCue(
  choices: readonly ChoiceListChoice[],
  protagonistId: string | undefined,
): ChoiceVisualCue | null {
  const explicitCue = choices.find((choice) => choice.visualCue !== undefined)?.visualCue;
  if (explicitCue !== undefined) return explicitCue;
  if (protagonistId === undefined) return null;
  return {
    characterId: protagonistId,
    actingVariantId: "",
    previewLayer: "cutin",
  };
}

function ChoiceOption({
  choice,
  index,
  variant,
  state,
  isPending,
  hasChosenContext,
  showEmotion,
  charactersMap,
  onTap,
}: ChoiceOptionProps): JSX.Element {
  return (
    <button
      key={choice.id}
      type="button"
      data-testid="choice-list-option"
      disabled={isPending}
      onClick={() => onTap(choice.id)}
      // 2段階タップ時のみ preview 状態をトグルとして SR に公開する。
      // backscroll 経路（1タップ確定）ではトグル意味論を持たないため付与しない。
      aria-pressed={hasChosenContext ? undefined : state.isPreviewed}
      data-chosen={state.chosenAttr}
      className={getChoiceButtonClassName(variant, state)}
      style={getChoiceOptionStyle(variant, index)}
    >
      <span
        className={getBadgeClassName(variant, state.isPreviewed)}
        aria-hidden="true"
      >
        {index + 1}
      </span>

      <span className={getTextStackClassName(variant)}>
        <span className={getChoiceTextClassName(variant)}>
          {parseInlineMarkup(choice.text, charactersMap)}
        </span>
        {showEmotion && !state.isPreviewed && shouldRenderInlineEmotion(variant) && (
          <span
            data-testid={`choice-emotion-${choice.id}`}
            className={getEmotionClassName(variant)}
            aria-hidden="true"
          >
            {parseInlineMarkup(choice.emotion ?? "", charactersMap)}
          </span>
        )}
        {state.isPreviewed && shouldRenderInlinePreviewHint(variant) && (
          <span className={getPreviewHintClassName(variant)} aria-hidden="true">
            もう一度タップして確定
          </span>
        )}
        {state.isChosen && (
          <span
            data-testid={`choice-chosen-marker-${choice.id}`}
            className="text-xs text-reader-warning-border font-medium"
            aria-hidden="true"
          >
            ✓ 選択済
          </span>
        )}
      </span>

      {!state.isNonChosen && (
        <span
          className={getArrowClassName(variant, state.isPreviewed)}
          aria-hidden="true"
        >
          {state.isPreviewed ? "✓" : "›"}
        </span>
      )}
    </button>
  );
}

export function ChoiceList({
  choices,
  isPending,
  onSelect,
  chosenChoiceId,
  charactersMap,
  variant = "default",
  choicePrompt,
  onPreviewChoiceChange,
}: ChoiceListProps): JSX.Element {
  const env = useMemo(() => loadClientEnv(), []);
  const hasChosenContext = chosenChoiceId !== undefined;
  const effectiveMap = charactersMap ?? EMPTY_CHARACTERS_MAP;

  // Folkwake 2段階タップ: 1タップ目で preview、2タップ目で確定。
  // backscroll 経路 (hasChosenContext) では無効化して即時 onSelect。
  const [previewedId, setPreviewedId] = useState<string | null>(null);

  const handleTap = (choiceId: string): void => {
    if (hasChosenContext) {
      onSelect(choiceId);
      return;
    }
    if (previewedId === choiceId) {
      // 2タップ目: 確定
      setPreviewedId(null);
      onSelect(choiceId);
    } else {
      // 1タップ目: preview に切り替え
      setPreviewedId(choiceId);
    }
  };

  // preview 中の choice。live region の読み上げテキスト生成に使う。
  const previewedChoice =
    !hasChosenContext && previewedId !== null
      ? choices.find((c) => c.id === previewedId)
      : undefined;
  const showCompactPreviewEmotion =
    shouldUseCompactPreviewEmotion(variant, previewedChoice, env.choiceEmotionEnabled);
  const shouldRenderLocalCompactPreview =
    showCompactPreviewEmotion && onPreviewChoiceChange === undefined;

  useEffect(() => {
    if (onPreviewChoiceChange === undefined || !shouldNotifyPreview(variant)) {
      return;
    }
    if (previewedChoice === undefined) {
      onPreviewChoiceChange(null);
      return;
    }
    onPreviewChoiceChange(getPreviewNotification(previewedChoice, env.choiceEmotionEnabled));
  }, [env.choiceEmotionEnabled, onPreviewChoiceChange, previewedChoice, variant]);

  useEffect(() => {
    return () => {
      onPreviewChoiceChange?.(null);
    };
  }, [onPreviewChoiceChange]);

  return (
    <div
      data-testid="choice-list"
      data-choice-list-variant={variant}
      className="flex flex-col gap-2"
    >
      {/* 2段階タップの preview 状態を SR に通知する live region（PR #677 レビュー NIT 対応）。
          compact の視覚ヒントは選択肢の補足行を aria-hidden で出すため、SR には
          ここで選択中テキスト + 確定方法を読み上げる。確定/解除で空文字に戻す。 */}
      <p
        aria-live="polite"
        className="sr-only"
        data-testid="choice-preview-status"
      >
        {previewedChoice !== undefined
          ? `「${stripInlineMarkup(previewedChoice.text)}」を選択中。もう一度押すと確定します`
          : ""}
      </p>
      {isStageSideVariant(variant) && choicePrompt !== undefined && choicePrompt.trim().length > 0 && (
        <p
          data-testid="choice-list-prompt"
          className={getChoicePromptClassName()}
        >
          {choicePrompt}
        </p>
      )}
      <div
        data-testid="choice-list-options"
        className={getChoiceListOptionsClassName(variant)}
      >
        {choices.map((choice, index) => {
          const state = getChoiceRenderState(
            choice.id,
            chosenChoiceId,
            previewedId,
            hasChosenContext,
          );
          return (
            <ChoiceOption
              key={choice.id}
              choice={choice}
              index={index}
              variant={variant}
              state={state}
              isPending={isPending}
              hasChosenContext={hasChosenContext}
              showEmotion={shouldShowEmotion(choice, env.choiceEmotionEnabled)}
              charactersMap={effectiveMap}
              onTap={handleTap}
            />
          );
        })}
      </div>
      {shouldRenderLocalCompactPreview && previewedChoice !== undefined && (
        <p
          data-testid="choice-preview-emotion"
          className={getCompactPreviewLineClassName()}
          aria-hidden="true"
        >
          <span aria-hidden="true">（</span>
          {parseInlineMarkup(previewedChoice.emotion ?? "", effectiveMap)}
          <span aria-hidden="true">）</span>
        </p>
      )}
    </div>
  );
}
